import React, { useState, useEffect } from "react";
import {
  Scale, Lock, Trash2, X, Archive, Loader2,
  ChevronRight, Search, SlidersHorizontal, Star, MoreVertical,
  CalendarDays, Building2, MapPin, Hourglass, AlarmClock, LayoutGrid, List, Eye,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { db } from "../firebase"; // Import the Firestore instance
import { collection, getDocs, query, where, deleteDoc, doc, writeBatch, setDoc, getDoc, updateDoc } from "firebase/firestore"; // Import Firestore functions
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { getUserLabels } from "../services/labelService";
import featureGatingService from "../services/featureGatingService";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";
import { getStatusBucket, priorityMeta } from "../utils/caseStatus";

import SelectMenu from "../components/crm/SelectMenu";
import { isCaseShared, useDisposeSharingPrompt } from "../components/DisposeSharingModal";
// ── Hearing-date helpers (data stores dates as "DD/MM/YYYY") ──
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const parseHearing = (str) => {
  if (!str || typeof str !== "string") return null;
  const [d, m, y] = str.split("/").map(Number);
  if (!d || !m || !y) return null;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return isNaN(dt.getTime()) ? null : dt;
};
const startOfToday = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};
const hearingDateValue = (c) => {
  const dt = parseHearing(c.nextHearingDate);
  return dt ? dt.getTime() : null;
};
const formatHearing = (str) => {
  const dt = parseHearing(str);
  if (!dt) return "Not scheduled";
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
};
const isOverdue = (c) => {
  const dt = parseHearing(c.nextHearingDate);
  return dt ? dt < startOfToday() : false;
};
const isUpcoming7 = (c) => {
  const dt = parseHearing(c.nextHearingDate);
  if (!dt) return false;
  const diff = Math.round((dt - startOfToday()) / 86400000);
  return diff >= 0 && diff <= 7;
};
// Priority now comes from the case's own `priority` field (set on AddCase) via
// priorityMeta() — it is no longer derived from the hearing date. Whether a
// hearing has lapsed is still answered by isOverdue() above, independently.
const partyTitle = (c) => {
  const p = (c.petitionerName || "").trim();
  const r = (c.respondentName || "").trim();
  if (p && r) return `${p} vs ${r}`;
  return p || r || "Untitled Case";
};

function PendingCases() {
  const { globalUser } = useAuth();
  const { status } = useParams();
  const [pendingCases, setPendingCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [disclosedCasesCount, setDisclosedCasesCount] = useState(0);
  const [disposedCasesCount, setDisposedCasesCount] = useState(0); // State for disposed cases count
  const [showDisposed, setShowdisposed] = useState(false);
  const [labels, setLabels] = useState([]);
  const [lockedCasesMap, setLockedCasesMap] = useState(new Map());
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lockedCaseClicked, setLockedCaseClicked] = useState(null);
  const [rawCaseData, setRawCaseData] = useState(new Map()); // Store raw case data for timestamp sorting
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [isSelectionMode, setIsSelectionMode] = useState(false); // Bulk delete mode
  const [selectedCases, setSelectedCases] = useState([]); // Selected cases for deletion
  const [showConfirmation, setShowConfirmation] = useState(false); // Confirmation modal
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false); // Delete All confirmation
  const [showDisposeAllConfirmation, setShowDisposeAllConfirmation] = useState(false);
  const [isDisposingAll, setIsDisposingAll] = useState(false);
  const [askKeepSharing, disposeSharingModal] = useDisposeSharingPrompt();

  // Toolbar / view state (search, court + case-type + priority filters, sort, layout)
  const [searchTerm, setSearchTerm] = useState("");
  const [courtFilter, setCourtFilter] = useState("all");
  const [caseTypeFilter, setCaseTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  // "View overdue" used to work by setting priorityFilter to "high", which only
  // held while priority was derived from the hearing date. Now that priority is
  // user-set, overdue needs its own filter or the link would show the wrong cases.
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sortBy, setSortBy] = useState("nextHearing");
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [bookmarked, setBookmarked] = useState(new Set());

  // Helper function to get label by ID
  const getLabelById = (labelId) => {
    return labels.find((label) => label.id === labelId);
  };

  // Helper function to sort cases: unlocked first, then locked; within each group sort by timestamp (newest first)
  const sortCasesByLockStatusAndTimestamp = (cases, lockMap, rawDataMap) => {
    return [...cases].sort((a, b) => {
      const aLocked = lockMap.get(a.id)?.isLocked || false;
      const bLocked = lockMap.get(b.id)?.isLocked || false;

      // First sort by lock status (unlocked first)
      if (aLocked !== bLocked) {
        return aLocked ? 1 : -1; // Unlocked cases come first
      }

      // Within the same lock status, sort by timestamp (newest first)
      const getTimeValue = (caseItem) => {
        const caseData = rawDataMap.get(caseItem.id);
        if (!caseData) return 0;

        // Check subscriptionMetadata.addedAt
        if (caseData.subscriptionMetadata?.addedAt) {
          const addedAt = caseData.subscriptionMetadata.addedAt;
          if (addedAt?.toMillis) return addedAt.toMillis();
          if (addedAt?.toDate) return addedAt.toDate().getTime();
          if (typeof addedAt === 'number') return addedAt;
        }

        // Check createdAt
        if (caseData.createdAt) {
          if (caseData.createdAt?.toMillis) return caseData.createdAt.toMillis();
          if (caseData.createdAt?.toDate) return caseData.createdAt.toDate().getTime();
          if (typeof caseData.createdAt === 'number') return caseData.createdAt;
        }

        // Check timestamp
        if (caseData.timestamp) {
          if (caseData.timestamp?.toMillis) return caseData.timestamp.toMillis();
          if (caseData.timestamp?.toDate) return caseData.timestamp.toDate().getTime();
          if (typeof caseData.timestamp === 'number') return caseData.timestamp;
        }

        return 0;
      };

      const aTime = getTimeValue(a);
      const bTime = getTimeValue(b);

      // Newest first (descending order)
      return bTime - aTime;
    });
  };

  // Fetch user labels
  useEffect(() => {
    const fetchLabels = async () => {
      if (!globalUser?.uid) return;
      try {
        const userLabels = await getUserLabels(globalUser.uid);
        setLabels(userLabels);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    };
    fetchLabels();
  }, [globalUser]);

  // Fetch pending cases and disclosed cases count from Firestore
  useEffect(() => {
    console.log("PENDING CASES STATE:", pendingCases);

    const fetchCases = async () => {
      if (!globalUser) {
        setIsLoading(false);
        return; // Early return if user is not authenticated
      }

      setIsLoading(true);

      let caseList = [];
      const pendingCollection = collection(db, "pending"); // Reference to the "pending" collection
      const disposedCollection = collection(db, "disposed"); // Reference to the "disposed" collection

      // Create a query to filter pending cases by the current user's ID
      const pendingCasesQuery = query(
        pendingCollection,
        where("owner", "==", globalUser.uid)
      );
      const disposedCasesQuery = query(
        disposedCollection,
        where("owner", "==", globalUser.uid)
      );

      // Fetch documents from the pending collection
      const pendingSnapshot = await getDocs(pendingCasesQuery);
      const pendingDocs = [];
      const rawDataMap = new Map(); // Store raw case data for timestamp sorting
      pendingSnapshot.forEach((doc) => {
        pendingDocs.push(doc);
        const caseData = doc.data(); // Get the data from the document
        rawDataMap.set(doc.id, caseData); // Store raw data for sorting
        const newCase = {
          id: doc.id, // Use the document ID
          caseNumber: caseData.caseNo,
          type: caseData.caseType,
          petitionerName: caseData.petitionerName,
          respondentName: caseData.respondentName,
          court: caseData.courtName,
          bench: caseData.bench || caseData.benchName || "",
          caseStage: caseData.caseStage || "",
          status: caseData.status || "Status not available", // Fetching status field
          priority: caseData.priority || "", // User-set on AddCase; "" renders as "Not set"
          nextHearingDate: caseData.nextHearingDate || null,
          labels: caseData.labels || [],
          labelsByUser: caseData.labelsByUser || {},
          collection: "pending", // Track which collection the case is from
        };
        caseList.push(newCase); // Add the new case to the list
      });
      setRawCaseData(rawDataMap); // Store raw data in state

      // Fetch documents from the disposed collection
      const disposedSnapshot = await getDocs(disposedCasesQuery);
      disposedSnapshot.forEach((doc) => {
        const caseData = doc.data(); // Get the data from the document
        const newCase = {
          id: doc.id, // Use the document ID
          caseNumber: caseData.caseNo,
          type: caseData.caseType,
          petitionerName: caseData.petitionerName,
          respondentName: caseData.respondentName,
          court: caseData.courtName,
          bench: caseData.bench || caseData.benchName || "",
          caseStage: caseData.caseStage || "",
          status: caseData.status || "Status not available", // Fetching status field
          priority: caseData.priority || "", // User-set on AddCase; "" renders as "Not set"
          nextHearingDate: caseData.nextHearingDate || null,
          labels: caseData.labels || [],
          labelsByUser: caseData.labelsByUser || {},
          collection: "disposed", // Track which collection the case is from
        };
        caseList.push(newCase); // Add the new case to the list
      });

      console.log(caseList)

      // Check lock status for pending cases and apply sorting
      let lockStatusMap = new Map();
      if (pendingDocs.length > 0) {
        try {
          lockStatusMap = await featureGatingService.getLockedCasesMap(globalUser.uid, pendingDocs);
          setLockedCasesMap(lockStatusMap);
          console.log('Lock status map:', lockStatusMap);
        } catch (error) {
          console.error('Error checking lock status:', error);
        }
      }

      // Filter and sort pending cases (unlocked first, then locked, newest first within each group)
      if (status === "pending") {
        // Show every case in the "pending" bucket (active / not a terminal outcome)
        const filteredPendingCases = caseList.filter(
          (caseItem) => getStatusBucket(caseItem.status) === "pending"
        );
        const sortedCases = sortCasesByLockStatusAndTimestamp(filteredPendingCases, lockStatusMap, rawDataMap);
        setPendingCases(sortedCases);
        setShowdisposed(true);
      }
      if (status === "pre Admission" || status === "admitted" || status === "adjourn" || status === "na") {
        const filteredPendingCases = caseList.filter(
          (caseItem) => caseItem.status.toLowerCase() === status.toLowerCase()
        );
        const sortedCases = sortCasesByLockStatusAndTimestamp(filteredPendingCases, lockStatusMap, rawDataMap);
        setPendingCases(sortedCases);
        setShowdisposed(false);
      }


      // Count disclosed cases (assuming "disclosed" status is used in the disposed collection)
      const disclosedCount = caseList.filter(
        (caseItem) => caseItem.status.toLowerCase() === "disclosed"
      ).length;
      setDisclosedCasesCount(disclosedCount);

      // Count disposed cases
      const disposedCount = caseList.filter(
        (caseItem) => getStatusBucket(caseItem.status) === "disposed"
      ).length;
      setDisposedCasesCount(disposedCount);

      setIsLoading(false);
    };

    fetchCases();
  }, [globalUser]);

  // Helper function to delete events and cancel reminders for a case
  const cleanupEventsAndReminders = async (caseNumber, userId) => {
    if (!caseNumber || !userId) {
      console.log("Missing caseNumber or userId for cleanup");
      return;
    }

    console.log(`Cleaning up events and reminders for case: ${caseNumber}`);

    try {
      const batch = writeBatch(db);
      let eventsDeleted = 0;
      let remindersDeleted = 0;

      // 1. Delete events from lawyers/{uid}/events where "Case No" matches
      const eventsQuery = query(
        collection(db, "lawyers", userId, "events"),
        where("Case No", "==", caseNumber)
      );
      const eventsSnapshot = await getDocs(eventsQuery);

      eventsSnapshot.docs.forEach((eventDoc) => {
        batch.delete(eventDoc.ref);
        eventsDeleted++;
      });

      // 2. Delete reminders from eventReminders where caseNo matches
      const remindersQuery = query(
        collection(db, "eventReminders"),
        where("caseNo", "==", caseNumber),
        where("recipientId", "==", userId)
      );
      const remindersSnapshot = await getDocs(remindersQuery);

      remindersSnapshot.docs.forEach((reminderDoc) => {
        batch.delete(reminderDoc.ref);
        remindersDeleted++;
      });

      // Commit the batch
      if (eventsDeleted > 0 || remindersDeleted > 0) {
        await batch.commit();
        console.log(`Cleanup complete: ${eventsDeleted} events deleted, ${remindersDeleted} reminders deleted`);
      } else {
        console.log("No events or reminders found to clean up");
      }
    } catch (error) {
      console.error("Error cleaning up events and reminders:", error);
    }
  };

  // Handle deletion of selected cases
  const handleDelete = async () => {
    setShowConfirmation(false);
    const pendingToDelete = selectedCases.filter((c) => c.collection === "pending").map((c) => c.id);
    const disposedToDelete = selectedCases.filter((c) => c.collection === "disposed").map((c) => c.id);

    try {
      // Get case numbers for cleanup before deleting
      const casesToCleanup = selectedCases.map((c) => c.caseNumber).filter(Boolean);

      // Clean up events and reminders for each case
      if (globalUser?.uid && casesToCleanup.length > 0) {
        await Promise.all(
          casesToCleanup.map((caseNumber) =>
            cleanupEventsAndReminders(caseNumber, globalUser.uid)
          )
        );
      }

      if (pendingToDelete.length > 0) {
        await Promise.all(pendingToDelete.map((id) => deleteDoc(doc(db, "pending", id))));
      }

      if (disposedToDelete.length > 0) {
        await Promise.all(disposedToDelete.map((id) => deleteDoc(doc(db, "disposed", id))));
      }

      setPendingCases((prevCases) => prevCases.filter((c) => !selectedCases.some((s) => s.id === c.id)));
      setSelectedCases([]);
      setIsSelectionMode(false);
    } catch (error) {
      console.error("Error deleting cases: ", error);
    }
  };

  // Handle deletion of all displayed cases
  const handleDeleteAll = async () => {
    setShowDeleteAllConfirmation(false);
    const pendingToDelete = pendingCases.filter((c) => c.collection === "pending").map((c) => c.id);
    const disposedToDelete = pendingCases.filter((c) => c.collection === "disposed").map((c) => c.id);

    try {
      // Clean up events and reminders for each case
      if (globalUser?.uid && pendingCases.length > 0) {
        await Promise.all(
          pendingCases
            .map((c) => c.caseNumber)
            .filter(Boolean)
            .map((caseNumber) => cleanupEventsAndReminders(caseNumber, globalUser.uid))
        );
      }

      if (pendingToDelete.length > 0) {
        await Promise.all(pendingToDelete.map((id) => deleteDoc(doc(db, "pending", id))));
      }
      if (disposedToDelete.length > 0) {
        await Promise.all(disposedToDelete.map((id) => deleteDoc(doc(db, "disposed", id))));
      }

      setPendingCases([]);
      setSelectedCases([]);
      setIsSelectionMode(false);
    } catch (error) {
      console.error("Error deleting all cases: ", error);
    }
  };

  // Handle dispose all pending cases
  const handleDisposeAll = async () => {
    setShowDisposeAllConfirmation(false);
    setIsDisposingAll(true);

    const pendingCasesToDispose = pendingCases.filter(
      (c) => c.collection === "pending"
    );

    if (pendingCasesToDispose.length === 0) {
      toast.error("No pending cases to dispose.");
      setIsDisposingAll(false);
      return;
    }

    try {
      // Shared cases in the batch: ask once whether sharing survives disposal.
      // The list rows don't carry `team`, so read it from the case docs.
      const teamSnaps = await Promise.all(
        pendingCasesToDispose.map((c) => getDoc(doc(db, "pending", c.id)))
      );
      const sharedCount = teamSnaps.filter(
        (snap) => snap.exists() && isCaseShared(snap.data(), globalUser?.uid)
      ).length;
      let keepSharing = false;
      if (sharedCount > 0) {
        const choice = await askKeepSharing(sharedCount);
        if (choice === null) return; // finally clears isDisposingAll
        keepSharing = choice;
      }

      for (const caseItem of pendingCasesToDispose) {
        const pendingCaseDoc = doc(db, "pending", caseItem.id);
        const disposedCaseDoc = doc(db, "disposed", caseItem.id);
        const pendingCaseSnapshot = await getDoc(pendingCaseDoc);

        if (pendingCaseSnapshot.exists()) {
          const caseDataToDispose = pendingCaseSnapshot.data();

          // Clean up events and reminders
          if (caseDataToDispose.caseNo && globalUser?.uid) {
            await cleanupEventsAndReminders(caseDataToDispose.caseNo, globalUser.uid);
          }

          // Remove shared members from default team if no other pending cases
          const sharedMembers = Array.isArray(caseDataToDispose.team)
            ? caseDataToDispose.team.filter((uid) => uid !== globalUser?.uid)
            : [];
          // Keep sharing: members stay on the case, so leave the default team alone.
          for (const memberUid of keepSharing ? [] : sharedMembers) {
            const otherPendingQuery = query(
              collection(db, "pending"),
              where("owner", "==", globalUser.uid),
              where("team", "array-contains", memberUid)
            );
            const otherPendingSnap = await getDocs(otherPendingQuery);
            const otherCases = otherPendingSnap.docs.filter((d) => d.id !== caseItem.id);
            if (otherCases.length === 0) {
              await featureGatingService.removeMemberFromDefaultTeam(globalUser.uid, memberUid);
            }
          }

          // Clear team on pending doc first
          if (!keepSharing && sharedMembers.length > 0) {
            await updateDoc(pendingCaseDoc, { team: [] });
          }

          // Move to disposed collection
          await setDoc(disposedCaseDoc, {
            ...caseDataToDispose,
            status: "disposed",
            // Keep sharing → carry `team` over so the disposed case stays shared.
            team: keepSharing ? caseDataToDispose.team || [] : [],
            previousHearingDate: caseDataToDispose.nextHearingDate || caseDataToDispose.previousHearingDate,
            nextHearingDate: null,
          });
          await deleteDoc(pendingCaseDoc);
        }
      }

      setPendingCases([]);
      setSelectedCases([]);
      setIsSelectionMode(false);
      toast.success(`${pendingCasesToDispose.length} case(s) disposed successfully.`);
    } catch (error) {
      console.error("Error disposing all cases: ", error);
      toast.error("Error disposing cases. Please try again.");
    } finally {
      setIsDisposingAll(false);
    }
  };

  // Reset to page 1 whenever the filtered result set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredCases]);

  // Apply toolbar search + filters + sort to build the displayed list
  useEffect(() => {
    let list = [...pendingCases];
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        [c.caseNumber, c.type, c.petitionerName, c.respondentName, c.court, c.bench]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    if (courtFilter !== "all") list = list.filter((c) => (c.court || "") === courtFilter);
    if (caseTypeFilter !== "all") list = list.filter((c) => (c.type || "") === caseTypeFilter);
    if (priorityFilter !== "all") list = list.filter((c) => priorityMeta(c.priority).key === priorityFilter);
    if (overdueOnly) list = list.filter(isOverdue);
    list.sort((a, b) => {
      if (sortBy === "caseNumber") return (a.caseNumber || "").localeCompare(b.caseNumber || "");
      if (sortBy === "recent") return 0; // preserve fetch order (already newest-first)
      const da = hearingDateValue(a);
      const dbv = hearingDateValue(b);
      if (da === null && dbv === null) return 0;
      if (da === null) return 1;
      if (dbv === null) return -1;
      return da - dbv; // soonest hearing first
    });
    setFilteredCases(list);
  }, [pendingCases, searchTerm, courtFilter, caseTypeFilter, priorityFilter, overdueOnly, sortBy]);

  // Toggle case selection
  const toggleCaseSelection = (caseItem) => {
    const isSelected = selectedCases.some((c) => c.id === caseItem.id);
    if (isSelected) {
      setSelectedCases(selectedCases.filter((c) => c.id !== caseItem.id));
    } else {
      setSelectedCases([...selectedCases, caseItem]);
    }
  };

  const next7Count = pendingCases.filter(isUpcoming7).length;
  const overdueCount = pendingCases.filter(isOverdue).length;
  const courtOptions = [...new Set(pendingCases.map((c) => c.court).filter(Boolean))].sort();
  const caseTypeOptions = [...new Set(pendingCases.map((c) => c.type).filter(Boolean))].sort();

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-3">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs">
          <Link to="/portal/dashboard" className="text-slate-500 hover:text-slate-800">Home</Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <Link to="/portal/allcases" className="text-slate-500 hover:text-slate-800">Cases</Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-medium text-slate-800">Pending Cases</span>
        </div>

        {/* Header */}
        <div className="mt-1.5">
          <h1 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">Pending Cases</h1>
          <p className="mt-0.5 text-xs text-slate-500">Cases awaiting their next hearing</p>
        </div>

        {/* Stat cards */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="flex items-center gap-3 rounded-xl border border-yellow-300 bg-yellow-50/70 p-3 shadow-sm">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-yellow-100 text-yellow-600">
              <Hourglass className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Pending Cases</p>
              <p className="text-xl font-bold text-slate-900">{pendingCases.length}</p>
              <button
                type="button"
                onClick={() => { setSearchTerm(""); setCourtFilter("all"); setCaseTypeFilter("all"); setPriorityFilter("all"); setOverdueOnly(false); }}
                className="text-xs font-medium text-yellow-700 hover:underline"
              >
                View all pending
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
              <CalendarDays className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Next 7 Days Hearings</p>
              <p className="text-xl font-bold text-slate-900">{next7Count}</p>
              <Link to="/portal/calendar" className="text-xs font-medium text-indigo-600 hover:underline">View calendar</Link>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-500">
              <AlarmClock className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Overdue Hearings</p>
              <p className="text-xl font-bold text-slate-900">{overdueCount}</p>
              <button type="button" onClick={() => setOverdueOnly((v) => !v)} className="text-xs font-medium text-rose-600 hover:underline">{overdueOnly ? "Show all" : "View overdue"}</button>
            </div>
          </div>

          <Link
            to="/portal/disposedcases/disposed"
            className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500">
              <Scale className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Disposed Cases</p>
              <p className="text-xl font-bold text-slate-900">{disposedCasesCount}</p>
              <span className="text-xs font-medium text-emerald-600">View disposed</span>
            </div>
          </Link>
        </div>

        {/* Toolbar */}
        <div className="mt-3 flex flex-col gap-2.5 lg:flex-row lg:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by case number, party, advocate, court..."
              className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SelectMenu
              value={courtFilter}
              onChange={(e) => setCourtFilter(e.target.value)}
              className="max-w-[150px] truncate rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm focus:border-yellow-400 focus:outline-none"
            >
              <option value="all">All Courts</option>
              {courtOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </SelectMenu>
            <SelectMenu
              value={caseTypeFilter}
              onChange={(e) => setCaseTypeFilter(e.target.value)}
              className="max-w-[150px] truncate rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm focus:border-yellow-400 focus:outline-none"
            >
              <option value="all">All Case Types</option>
              {caseTypeOptions.map((t) => (<option key={t} value={t}>{t}</option>))}
            </SelectMenu>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <SlidersHorizontal className="h-4 w-4" /> Filters
              </button>
              {showFilters && (
                <div className="absolute left-0 z-30 mt-2 w-52 rounded-lg border border-slate-200 bg-white p-3 shadow-xl">
                  <p className="mb-1.5 text-xs font-semibold text-slate-500">Priority</p>
                  <div className="flex flex-wrap gap-1">
                    {[["all", "All"], ["high", "High"], ["medium", "Medium"], ["low", "Low"], ["unset", "Not set"]].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setPriorityFilter(val)}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${priorityFilter === val ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 lg:ml-auto">
            <SelectMenu
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="max-w-[190px] truncate rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm focus:border-yellow-400 focus:outline-none"
            >
              <option value="nextHearing">Sort by: Next Hearing</option>
              <option value="recent">Sort by: Recently Added</option>
              <option value="caseNumber">Sort by: Case Number</option>
            </SelectMenu>
            <div className="flex items-center rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
              <button type="button" onClick={() => setViewMode("grid")} title="Grid view" className={`flex h-7 w-7 items-center justify-center rounded-md ${viewMode === "grid" ? "bg-yellow-100 text-yellow-700" : "text-slate-500 hover:bg-slate-50"}`}>
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setViewMode("list")} title="List view" className={`flex h-7 w-7 items-center justify-center rounded-md ${viewMode === "list" ? "bg-yellow-100 text-yellow-700" : "text-slate-500 hover:bg-slate-50"}`}>
                <List className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => { setIsSelectionMode((v) => !v); setSelectedCases([]); }}
              title="Select cases to delete"
              className={`flex h-8 w-8 items-center justify-center rounded-lg border shadow-sm ${isSelectionMode ? "border-red-300 bg-red-50 text-red-600" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setShowDisposeAllConfirmation(true)}
              title="Dispose all pending cases"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50"
            >
              <Archive className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Selection action bar */}
        {isSelectionMode && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5">
            <span className="text-sm font-medium text-red-700">{selectedCases.length} selected</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={selectedCases.length === 0} onClick={() => setShowConfirmation(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                <Trash2 className="h-4 w-4" /> Delete
              </button>
              <button type="button" onClick={() => setShowDeleteAllConfirmation(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50">
                Delete All
              </button>
              <button type="button" onClick={() => { setIsSelectionMode(false); setSelectedCases([]); }} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Case Cards */}
        <div className="mt-4">
          {/* Skeleton Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="h-52 animate-pulse rounded-xl border border-slate-200 bg-white"></div>
              ))}
            </div>
          )}

          {/* No cases message */}
          {!isLoading && pendingCases.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-20 text-center shadow-sm">
              <Scale className="mb-4 h-14 w-14 text-slate-300" />
              <p className="text-lg font-medium text-slate-700">No {status} cases found.</p>
              <p className="mt-1 text-sm text-slate-500">Cases awaiting their next hearing will appear here.</p>
            </div>
          )}

          {/* No results from filter, but cases exist */}
          {!isLoading && pendingCases.length > 0 && filteredCases.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-center shadow-sm">
              <Search className="mb-3 h-10 w-10 text-slate-300" />
              <p className="text-base font-medium text-slate-700">No cases match your current filters.</p>
              <p className="mt-1 text-sm text-slate-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}

          {/* Cases list */}
          {!isLoading && filteredCases.length > 0 && (
            <>
            {/* Outside-click catcher for card menus */}
            {openMenuId && <div className="fixed inset-0 z-20" onClick={() => setOpenMenuId(null)} />}
            <div className={viewMode === "grid"
              ? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
              : "grid grid-cols-1 gap-2.5"}>
              {filteredCases.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((caseItem) => {
                const userLabelIds = caseItem.labelsByUser?.[globalUser?.uid] || caseItem.labels || [];
                const caseLabels = userLabelIds.map(getLabelById).filter(Boolean);

                const lockStatus = lockedCasesMap.get(caseItem.id);
                const isLocked = lockStatus?.isLocked || false;
                const isSelected = selectedCases.some((c) => c.id === caseItem.id);
                const navigable = !isSelectionMode && !isLocked;

                const overdue = isOverdue(caseItem);
                const pr = priorityMeta(caseItem.priority);
                const isBookmarked = bookmarked.has(caseItem.id);

                const onCardClick = () => {
                  if (isSelectionMode) { toggleCaseSelection(caseItem); return; }
                  if (isLocked) { setLockedCaseClicked(caseItem); setShowUpgradeModal(true); }
                };

                return (
                  <div
                    key={caseItem.id}
                    onClick={onCardClick}
                    className={`relative flex flex-col rounded-xl border bg-white p-3 shadow-sm transition-shadow ${navigable ? "cursor-pointer hover:shadow-md" : ""} ${
                      isSelectionMode && isSelected ? "border-yellow-400 ring-2 ring-yellow-400/40" : "border-slate-200"
                    } ${isLocked ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {/* Stretched link for whole-card navigation */}
                    {navigable && (
                      <Link
                        to={`/portal/case/${caseItem.id}?from=pending`}
                        className="absolute inset-0 z-0 rounded-xl"
                        aria-label={`Open case ${caseItem.caseNumber || ""}`}
                      />
                    )}

                    {/* Lock overlay */}
                    {isLocked && (
                      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-black/40">
                        <div className="rounded-full bg-red-600 p-3">
                          <Lock size={26} color="white" />
                        </div>
                      </div>
                    )}

                    {/* Top row: status badge + actions */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {isSelectionMode && (
                          <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${isSelected ? "border-yellow-500 bg-yellow-500 text-white" : "border-slate-300 bg-white"}`}>
                            {isSelected && (
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                            )}
                          </span>
                        )}
                        <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${overdue ? "bg-rose-100 text-rose-700" : "bg-yellow-100 text-yellow-700"}`}>
                          {overdue ? "Overdue" : "Pending"}
                        </span>
                        {caseLabels.slice(0, 1).map((label) => (
                          <span key={label.id} className="rounded px-1.5 py-0.5 text-[9px] font-semibold text-white" style={{ backgroundColor: label.color }}>
                            {label.name}
                          </span>
                        ))}
                      </div>
                      <div className="relative z-10 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setBookmarked((prev) => {
                              const next = new Set(prev);
                              next.has(caseItem.id) ? next.delete(caseItem.id) : next.add(caseItem.id);
                              return next;
                            });
                          }}
                          title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                          className="rounded-md p-1 text-slate-400 hover:bg-slate-50 hover:text-yellow-500"
                        >
                          <Star className={`h-4 w-4 ${isBookmarked ? "fill-yellow-400 text-yellow-400" : ""}`} />
                        </button>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === caseItem.id ? null : caseItem.id); }}
                            title="More"
                            className="rounded-md p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openMenuId === caseItem.id && (
                            <div className="absolute right-0 z-30 mt-1 w-40 rounded-lg border border-slate-200 bg-white py-1 shadow-xl" onClick={(e) => e.stopPropagation()}>
                              <Link to={`/portal/case/${caseItem.id}?from=pending`} className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                                <Eye className="h-3.5 w-3.5" /> View details
                              </Link>
                              <button
                                type="button"
                                onClick={() => { setOpenMenuId(null); setIsSelectionMode(true); toggleCaseSelection(caseItem); }}
                                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" /> Select to delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Case number */}
                    <p className="mt-2 text-[11px] font-semibold text-slate-700">{caseItem.caseNumber || "—"}</p>

                    {/* Party title */}
                    <h3 className="mt-0.5 line-clamp-2 text-[13px] font-bold leading-snug text-slate-900" title={partyTitle(caseItem)}>
                      {partyTitle(caseItem)}
                    </h3>

                    {/* Court + bench */}
                    <div className="mt-2 space-y-0.5">
                      <p className="flex items-center gap-1.5 truncate text-[11px] text-slate-600" title={caseItem.court || ""}>
                        <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        {caseItem.court || "—"}
                      </p>
                      {caseItem.bench && (
                        <p className="flex items-center gap-1.5 truncate text-[11px] text-slate-500" title={caseItem.bench}>
                          <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          {caseItem.bench}
                        </p>
                      )}
                    </div>

                    <div className="my-2 border-t border-slate-100" />

                    {/* Next hearing + priority */}
                    <div className="flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          <CalendarDays className="h-3.5 w-3.5" /> Next Hearing
                        </p>
                        <p className={`mt-0.5 truncate text-xs font-semibold ${overdue ? "text-rose-600" : "text-slate-800"}`}>
                          {formatHearing(caseItem.nextHearingDate)}
                        </p>
                        {caseItem.caseStage && (
                          <p className="truncate text-[11px] text-slate-500">{caseItem.caseStage}</p>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-[11px] text-slate-400">Priority</p>
                        <p className={`mt-0.5 flex items-center justify-end gap-1.5 text-xs font-medium ${pr.text}`}>
                          <span className={`h-2 w-2 rounded-full ${pr.dot}`} /> {pr.label}
                        </p>
                      </div>
                    </div>

                    {/* View details button */}
                    {navigable ? (
                      <Link
                        to={`/portal/case/${caseItem.id}?from=pending`}
                        onClick={(e) => e.stopPropagation()}
                        className="relative z-10 mt-3 inline-flex w-full items-center justify-center rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] font-semibold text-indigo-600 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-indigo-50"
                      >
                        View Details
                      </Link>
                    ) : (
                      <span className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] font-semibold text-slate-400 ring-1 ring-inset ring-slate-200">
                        View Details
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <Pagination
              currentPage={currentPage}
              totalItems={filteredCases.length}
              onPageChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
            </>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h3 className="text-xl font-semibold mb-4 text-purple-600">Confirm Deletion</h3>
            <p className="text-sm mb-6">Are you sure you want to delete {selectedCases.length} selected case(s)? This action cannot be undone.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h3 className="text-xl font-semibold mb-2 text-red-600">Delete All Cases</h3>
            <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
            <p className="text-sm mb-6">
              Are you sure you want to delete <span className="font-semibold text-red-600">all {pendingCases.length} case(s)</span>? This will permanently remove all cases shown on this page.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteAllConfirmation(false)}
                className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                className="px-4 py-2 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {disposeSharingModal}

      {/* Dispose All Confirmation Modal */}
      {showDisposeAllConfirmation && (
        <div className=" fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="dispose-modal bg-[#0B0A29] text-white p-6 rounded-lg shadow-xl w-full max-w-sm border border-gray-700">
            <h3 className="text-xl font-semibold mb-2 text-yellow-500">Dispose All Pending Cases</h3>
            <p className="text-sm text-white mb-6">
              Are you sure you want to dispose <span className="font-bold text-white">all {pendingCases.filter((c) => c.collection === "pending").length} pending case(s)</span>? Their status will be changed to disposed and reminders will be cleared. For shared cases you will be asked whether to keep or revoke sharing.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDisposeAllConfirmation(false)}
                className="px-4 py-2 rounded text-sm font-medium bg-white text-black hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDisposeAll}
                disabled={isDisposingAll}
                className="px-4 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isDisposingAll && <Loader2 className="w-4 h-4 animate-spin" />}
                {isDisposingAll ? "Disposing..." : "Dispose All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1535] rounded-xl border border-red-500 max-w-md w-full p-6">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-600 rounded-full p-4 mb-4">
                <Lock size={40} color="white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Case Locked</h2>
              <p className="text-gray-300 mb-4">
                This case was added during your subscription period and is now locked because your subscription has expired.
              </p>
              <div className="bg-[#2d2a5a] rounded-lg p-4 mb-6 w-full">
                <p className="text-sm text-gray-400 mb-2">
                  <span className="font-semibold text-white">Case:</span> {lockedCaseClicked?.caseNumber}
                </p>
                <p className="text-sm text-gray-400">
                  <span className="font-semibold text-white">Party:</span> {lockedCaseClicked?.petitionerName}
                </p>
              </div>
              <p className="text-yellow-400 font-semibold mb-6">
                Upgrade your plan to unlock this case and access all your cases!
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition"
                >
                  Close
                </button>
                <Link
                  to="/portal/subscription"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-4 rounded-lg transition text-center"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingCases;
